export interface ExcelColumn {
  /** Column header text shown in the first Excel row. */
  header: string;
  /** Key used to look up the value in each row object. */
  key: string;
}

export type ExcelCellValue = string | number | null | undefined;

export interface ExportMeta {
  /** Software name, e.g. "Easy Call". */
  appName: string;
  orgName?: string | null;
  orgSlug?: string | null;
  /** When the export was generated. */
  generatedAt: Date;
}

/** Filesystem-safe timestamp for file names: `YYYY-MM-DD_HH-mm`. */
export function fileTimestamp(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(d.getHours())}-${p(
    d.getMinutes()
  )}`;
}

function orgLine(meta: ExportMeta): string {
  if (meta.orgName && meta.orgSlug) return `${meta.orgName} (${meta.orgSlug})`;
  return meta.orgName || meta.orgSlug || '';
}

/**
 * Build an .xlsx workbook in the browser and trigger a download.
 *
 * Uses SheetJS so the file opens cleanly in Excel with Hebrew (UTF-8 throughout)
 * and string cells (e.g. phone numbers with a leading zero) keep their leading
 * zeros instead of being coerced to numbers like a CSV would.
 *
 * When `meta` is supplied, a small title block (software name, organization,
 * generation timestamp) is prepended to the sheet.
 */
export async function exportToXlsx(opts: {
  fileName: string;
  sheetName?: string;
  columns: ExcelColumn[];
  rows: Record<string, ExcelCellValue>[];
  meta?: ExportMeta;
}): Promise<void> {
  const XLSX = await import('xlsx');
  const { fileName, sheetName = 'נתונים', columns, rows, meta } = opts;

  const aoa: ExcelCellValue[][] = [];

  if (meta) {
    aoa.push([meta.appName]);
    aoa.push(['ארגון', orgLine(meta)]);
    aoa.push(['נוצר בתאריך', meta.generatedAt.toLocaleString('he-IL')]);
    aoa.push([]); // spacer before the data header
  }

  aoa.push(columns.map((c) => c.header));
  for (const row of rows) {
    aoa.push(
      columns.map((c) => {
        const v = row[c.key];
        return v === null || v === undefined ? '' : v;
      })
    );
  }

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  // Approximate column widths from header + content length (account for the meta block too).
  ws['!cols'] = columns.map((c, i) => {
    let max = c.header.length;
    if (meta && i === 0) max = Math.max(max, 'נוצר בתאריך'.length);
    if (meta && i === 1) max = Math.max(max, orgLine(meta).length, meta.generatedAt.toLocaleString('he-IL').length);
    for (const row of rows) {
      const v = row[c.key];
      const len = v === null || v === undefined ? 0 : String(v).length;
      if (len > max) max = len;
    }
    return { wch: Math.min(60, Math.max(12, max + 2)) };
  });

  const wb = XLSX.utils.book_new();
  if (meta) {
    wb.Props = {
      Title: fileName,
      Company: meta.appName,
      CreatedDate: meta.generatedAt
    };
  }
  XLSX.utils.book_append_sheet(wb, ws, (sheetName || 'נתונים').slice(0, 31));

  const safeName = fileName.toLowerCase().endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  XLSX.writeFile(wb, safeName, { bookType: 'xlsx', compression: true });
}
